import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Avatar,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { useNavigate } from "react-router-dom";
import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import { useAdminAccess } from "../context/AdminAccessContext";
import {
  deleteBlogPost,
  getAllBlogPostsForAdmin,
  publishBlogPost,
  unpublishBlogPost,
} from "../services/blogService";

const requiredPublishFields = [
  "title",
  "slug",
  "excerpt",
  "content",
  "category",
  "coverImageUrl",
  "seoTitle",
  "seoDescription",
];

function formatDate(value) {
  if (!value) return "-";
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
}

function StatusBadge({ status }) {
  const isPublished = status === "published";
  return (
    <Chip
      size="small"
      label={isPublished ? "Published" : "Draft"}
      color={isPublished ? "success" : "default"}
      sx={{ fontWeight: 700 }}
    />
  );
}

export default function AdminBlogManagement() {
  const navigate = useNavigate();
  const { isMarketing } = useAdminAccess();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAllBlogPostsForAdmin();
      setPosts(data);
    } catch (err) {
      console.error("Failed to load blog posts:", err);
      setError("Could not load blog posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handlePublishToggle = async (post) => {
    try {
      setActionLoading(post.id);
      setError("");
      if (post.status === "published") {
        await unpublishBlogPost(post.id);
        setSuccess("Post moved back to draft.");
      } else {
        const missingFields = requiredPublishFields.filter((field) => !String(post[field] || "").trim());
        if (missingFields.length > 0) {
          setError(`Before publishing, complete: ${missingFields.join(", ")}.`);
          return;
        }
        await publishBlogPost(post.id);
        setSuccess("Post published.");
      }
      await fetchPosts();
    } catch (err) {
      console.error("Failed to update blog status:", err);
      setError("Could not update the post status.");
    } finally {
      setActionLoading("");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setActionLoading(deleteTarget.id);
      setError("");
      await deleteBlogPost(deleteTarget.id);
      setDeleteTarget(null);
      setSuccess("Post deleted.");
      await fetchPosts();
    } catch (err) {
      console.error("Failed to delete blog post:", err);
      setError("Could not delete the post.");
    } finally {
      setActionLoading("");
    }
  };

  const visiblePosts = posts.filter((post) => {
    const statusMatch = activeTab === "all" || post.status === activeTab;
    const text = `${post.title || ""} ${post.slug || ""} ${post.category || ""}`.toLowerCase();
    return statusMatch && text.includes(searchTerm.toLowerCase());
  });

  return (
    <>
      <AdminTopBar marketingMode={isMarketing} />
      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          mt: "64px",
          backgroundColor: "#ffffffff",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        {!isMarketing && <AdminNavbar />}
        <Box component="main" sx={{ flexGrow: 1, overflowY: "auto", p: { xs: 1, md: 2 }, direction: "ltr" }}>
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  width: 46,
                  height: 46,
                  boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
              >
                <ArticleOutlinedIcon />
              </Avatar>
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Blog Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Create SEO articles for tutors, schools, and Darssy growth campaigns.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate("/06071966/blogs/new")}
                sx={{ borderRadius: 2 }}
              >
                Create New Post
              </Button>
            </Box>
          </Card>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 0.75,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: "fit-content",
              }}
            >
              {[
                { value: "all", label: "All Posts", activeBg: "#eef2ff", activeColor: "#4f46e5" },
                { value: "published", label: "Published", activeBg: "#dcfce7", activeColor: "#16a34a" },
                { value: "draft", label: "Drafts", activeBg: "#fff7ed", activeColor: "#ea580c" },
              ].map((tab) => (
                <Button
                  key={tab.value}
                  disableRipple
                  onClick={() => setActiveTab(tab.value)}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 700,
                    textTransform: "none",
                    backgroundColor: activeTab === tab.value ? tab.activeBg : "transparent",
                    color: activeTab === tab.value ? tab.activeColor : "text.secondary",
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", direction: "ltr" }}>
              <TextField
                size="small"
                placeholder="Search by title, slug, or category..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                sx={{
                  width: 340,
                  backgroundColor: "#f9fafb",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  backgroundColor: "#fff",
                  minWidth: 42,
                  "&:hover": { backgroundColor: "#f9fafb", borderColor: "rgba(0,0,0,0.18)" },
                }}
              >
                <TuneIcon sx={{ color: "rgba(0,0,0,0.6)" }} />
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#ffffff",
              boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {loading ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: "#6b7280" }}>Loading blog posts...</Typography>
              </Box>
            ) : visiblePosts.length === 0 ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <ArticleOutlinedIcon sx={{ fontSize: 48, color: "#9ca3af" }} />
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 1 }}>
                  No blog posts yet
                </Typography>
                <Typography sx={{ color: "#6b7280", mt: 0.5 }}>
                  Start with a draft article for tutors or schools.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table sx={{ direction: "ltr" }}>
                  <TableHead sx={{ bgcolor: "#ffffff" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Post</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Cover</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Published</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visiblePosts.map((post) => (
                      <TableRow
                        key={post.id}
                        hover
                        sx={{
                          "& td": { py: 2 },
                          "&:hover": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell sx={{ textAlign: "left", maxWidth: 380 }}>
                          <Typography sx={{ fontWeight: 700, color: "#111827" }}>{post.title || "Untitled draft"}</Typography>
                          <Typography variant="body2" sx={{ color: "#6b7280", direction: "ltr", textAlign: "left" }}>{post.slug || "No slug"}</Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "left" }}>
                          {post.coverImageUrl ? (
                            <Box
                              component="img"
                              src={post.coverImageUrl}
                              alt={post.title || "Blog cover"}
                              sx={{ width: 96, height: 62, objectFit: "cover", borderRadius: 2 }}
                            />
                          ) : (
                            <Box sx={{ width: 96, height: 62, borderRadius: 2, bgcolor: "#eef2f7" }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left" }}>{post.category || "-"}</TableCell>
                        <TableCell sx={{ textAlign: "left" }}><StatusBadge status={post.status} /></TableCell>
                        <TableCell sx={{ textAlign: "left" }}>{formatDate(post.createdAt)}</TableCell>
                        <TableCell sx={{ textAlign: "left" }}>{formatDate(post.publishedAt)}</TableCell>
                        <TableCell sx={{ textAlign: "left", whiteSpace: "nowrap" }}>
                          <IconButton onClick={() => navigate(`/06071966/blogs/edit/${post.id}`)} aria-label="Edit">
                            <EditOutlinedIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handlePublishToggle(post)}
                            disabled={actionLoading === post.id}
                            aria-label={post.status === "published" ? "Unpublish" : "Publish"}
                          >
                            {post.status === "published" ? <UnpublishedOutlinedIcon /> : <PublishOutlinedIcon />}
                          </IconButton>
                          <IconButton
                            onClick={() => setDeleteTarget(post)}
                            disabled={actionLoading === post.id}
                            color="error"
                            aria-label="Delete"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete blog post?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#4b5563" }}>
            This will permanently delete "{deleteTarget?.title || "Untitled draft"}".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={Boolean(actionLoading)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
